import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { z } from "zod";
import { type Binding, BindingEnum } from "@/app/types";

const scopesSchema = z.array(BindingEnum).catch([]).default([]);

/**
 * The scopes a resource list is narrowed to; empty lists every scope.
 * Spread into the search schema of every route whose list offers the scope
 * filter, and strip the default so an unfiltered list keeps a clean URL.
 */
export const scopeFilterSearchSchema = z.object({ scope: scopesSchema });

export const scopeFilterSearchDefaults: z.infer<
  typeof scopeFilterSearchSchema
> = {
  scope: [],
};

/** Most specific first: a project resource overrides an organization one, which overrides a global one. */
const PRECEDENCE: Binding[] = ["project", "tenant", "static"];

export const scopeRank = (scope: Binding): number => {
  const rank = PRECEDENCE.indexOf(scope);
  return rank === -1 ? PRECEDENCE.length : rank;
};

/** Whether a list filtered to `selected` shows items in `scope`; an empty selection shows every scope. */
export const showsScope = (selected: Binding[], scope: Binding): boolean =>
  selected.length === 0 || selected.includes(scope);

/** The items whose scope is selected (every item when none is), most specific scope first. */
export const selectScopes = <T>(
  items: T[],
  selected: Binding[],
  scopeOf: (item: T) => Binding
): T[] =>
  items
    .filter((item) => showsScope(selected, scopeOf(item)))
    .toSorted((a, b) => scopeRank(scopeOf(a)) - scopeRank(scopeOf(b)));

/** The scopes at least one item belongs to. */
export const scopesWithItems = <T>(
  items: T[],
  scopeOf: (item: T) => Binding
): Binding[] => [...new Set(items.map(scopeOf))];

/**
 * Whether a scope filter can change what the list shows: items span more than
 * one scope, or a filter is already applied and must stay clearable.
 */
export const offersScopeFilter = (
  available: Binding[],
  selected: Binding[]
): boolean => available.length > 1 || selected.length > 0;

/** Adds `scope` to the selection when it is missing, and removes it otherwise. */
export const toggleScope = (selected: Binding[], scope: Binding): Binding[] =>
  selected.includes(scope)
    ? selected.filter((s) => s !== scope)
    : [...selected, scope];

/**
 * The filter to apply once a resource is saved to `saved`, so the list shows
 * it: the current filter when it already does, otherwise that filter plus
 * `saved`. Takes the raw search value, which may be missing or malformed.
 */
export const revealScope = (current: unknown, saved: Binding): Binding[] => {
  const selected = scopesSchema.parse(current);
  return showsScope(selected, saved) ? selected : [...selected, saved];
};

/**
 * Items shadowed by a more specific item with the same key, and items that
 * shadow a less specific one without being shadowed themselves.
 */
export const findOverrides = <T extends { binding: { type: Binding } }>(
  items: T[],
  keyOf: (item: T) => string | undefined
) => {
  const rankRange = new Map<string, { min: number; max: number }>();
  for (const item of items) {
    const key = keyOf(item);
    if (key === undefined) {
      continue;
    }
    const rank = scopeRank(item.binding.type);
    const range = rankRange.get(key);
    rankRange.set(key, {
      min: Math.min(range?.min ?? rank, rank),
      max: Math.max(range?.max ?? rank, rank),
    });
  }

  const overridden = new Set<T>();
  const overriding = new Set<T>();
  for (const item of items) {
    const key = keyOf(item);
    const range = key === undefined ? undefined : rankRange.get(key);
    if (!range) {
      continue;
    }
    const rank = scopeRank(item.binding.type);
    if (rank > range.min) {
      overridden.add(item);
    } else if (rank < range.max) {
      overriding.add(item);
    }
  }
  return { overridden, overriding };
};

/** The scope filter of the current route's search, and setters that keep it in the URL. */
export const useScopeFilter = () => {
  const current = useSearch({
    strict: false,
    select: (search) => search.scope,
  });
  const navigate = useNavigate();

  const setSelected = useCallback(
    (next: Binding[]) =>
      navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({ ...prev, scope: next }),
        replace: true,
      }),
    [navigate]
  );

  const reveal = useCallback(
    (saved: Binding) =>
      navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          scope: revealScope(prev.scope, saved),
        }),
        replace: true,
      }),
    [navigate]
  );

  const selected = useMemo(() => scopesSchema.parse(current), [current]);

  return { selected, setSelected, reveal };
};
